import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api } from "../../lib/api";
import { useRoleLists } from "../../lib/roleLists";
import { CoachRoleForm, EMPTY_ENTRY } from "./CoachRoleForm";
import { CoachEntryCard } from "./CoachEntryCard";

const toForm = (e) => ({ ...EMPTY_ENTRY, ...Object.fromEntries(Object.keys(EMPTY_ENTRY).map(k => [k, e[k] ?? EMPTY_ENTRY[k]])) });

// Step 4 · Current Role: coach entries (one per team), entered by admins. Side by side when there are several; edit, status, notes.
export function CurrentRole({ playerId, admin, onEntriesChange }) {
  const lists = useRoleLists();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | entry
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.coachEntries(playerId).then(d => { setData(d); onEntriesChange?.(d); }).catch(() => setData({ entries: [] }));
  }, [playerId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  // Mark coach-submitted entries as seen once the admin has them on screen.
  useEffect(() => {
    if (!admin || !data) return;
    const fresh = data.entries.filter(e => !e.reviewedAt);
    if (fresh.length) Promise.all(fresh.map(e => api.patchCoachEntry(playerId, e.id, { reviewed: true }))).then(() => setTimeout(load, 4000));
  }, [admin, data?.entries?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (e, body) => { try { await api.patchCoachEntry(playerId, e.id, body); load(); } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); } };
  const startNew = () => { setForm(EMPTY_ENTRY); setEditing("new"); };
  const startEdit = (e) => { setForm(toForm(e)); setEditing(e); };
  const submit = async () => {
    setBusy(true);
    const body = { ...form, coachEmail: form.coachEmail || null };
    try {
      if (editing === "new") await api.createCoachEntry(playerId, body); else await api.patchCoachEntry(playerId, editing.id, { fields: body });
      toast.success(editing === "new" ? "Coach entry saved" : "Entry updated"); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save entry"); } finally { setBusy(false); }
  };

  if (!lists.ready) return <div className="text-[#757575] text-xs" data-testid="role-lists-loading">{lists.error || "Loading role lists…"}</div>;
  const entries = data?.entries || [];
  const current = entries.filter(e => e.status === "current"), past = entries.filter(e => e.status !== "current");

  return (
    <div className="space-y-4" data-testid="current-role">
      {admin && !editing && (
        <button onClick={startNew} className="px-3 py-2 text-[10px] uppercase tracking-[0.3em] border border-white/15 text-[#c0c0c0] hover:text-white hover:border-white/30 flex items-center gap-1.5" data-testid="coach-entry-add-btn"><Plus className="w-3 h-3" /> Add coach entry</button>
      )}
      {admin && editing && (
        <div className="border border-[var(--org-accent)]/40 bg-[#0f0f0f] p-4 md:p-6" data-testid="coach-entry-editor">
          <div className="flex items-center justify-between mb-5">
            <div className="text-[10px] uppercase tracking-[0.35em] font-bold text-[var(--org-accent)]">{editing === "new" ? "New coach entry" : `Edit · ${editing.team} · ${editing.season}`}</div>
            <button onClick={() => setEditing(null)} className="text-[#a0a0a0] hover:text-white" data-testid="coach-entry-editor-close"><X className="w-4 h-4" /></button>
          </div>
          <CoachRoleForm value={form} onChange={setForm} onSubmit={submit} submitting={busy} submitLabel={editing === "new" ? "Save entry" : "Save changes"} />
        </div>
      )}
      {data && entries.length === 0 && !editing && <div className="text-[#757575] text-sm" data-testid="coach-entries-empty">{admin ? "No coach entry yet — add one." : "No coach input yet."}</div>}
      {current.length > 0 && (
        <div className={`grid gap-4 ${current.length > 1 ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`} data-testid="coach-entries-current">
          {current.map(e => <CoachEntryCard key={e.id} entry={e} admin={admin} onPatch={(b) => patch(e, b)} onEdit={() => startEdit(e)} />)}
        </div>
      )}
      {past.length > 0 && (
        <details className="group" data-testid="coach-entries-past">
          <summary className="cursor-pointer text-[10px] uppercase tracking-[0.3em] text-[#757575] hover:text-white">Past teams · {past.length}</summary>
          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2 mt-3">{past.map(e => <CoachEntryCard key={e.id} entry={e} admin={admin} onPatch={(b) => patch(e, b)} onEdit={() => startEdit(e)} />)}</div>
        </details>
      )}
      {data?.assessment && entries.length > 0 && <div className="text-[10px] text-[#575757]">Ratings compared against: {data.assessment.label || "latest assessment"}</div>}
    </div>
  );
}
